import RGL from 'react-grid-layout';
import { useForm } from 'react-hook-form';

import { Dashboard } from 'types/dashboard';
import { IframeWidget } from 'types/dashboard_widget';

import {
  getNewWidgetLayout,
  upsertWidget,
} from 'dashboards/services/dashboard';
import { useUpdateDashboard } from 'dashboards/hooks/useUpdateDashboard';
import { useModal } from 'hooks/useModal';
import { useDashboard } from 'dashboards/hooks/useDashboard';

import { Field } from 'components/Field/Field';
import { Input } from 'components/Input/Input';

import { WidgetFormHeader } from './Header';
import { WidgetFormFooter } from './Footer';

type IframeWidgetFieldValues = { name: string; url: string };

type IframeFormProps = { widget?: IframeWidget; dashboardId: string };

export function IframeForm({ widget, dashboardId }: IframeFormProps) {
  const { close } = useModal();

  const { data: dashboard } = useDashboard(dashboardId);

  const { mutate, isLoading: isUpdating } = useUpdateDashboard({
    onSuccess(data) {
      resetForm(data);
      close();
    },
  });

  const {
    formState: { errors, dirtyFields },
    handleSubmit,
    register,
    reset,
  } = useForm<IframeWidgetFieldValues>({
    defaultValues: { name: widget?.name, url: widget?.url },
  });

  const isDirty = Boolean(Object.keys(dirtyFields).length);

  function resetForm(updatedDashboard: Dashboard) {
    if (!widget?.id) {
      return;
    }

    const updatedWidget = updatedDashboard.configuration.widgets.find(
      ({ id }) => id === widget.id
    ) as IframeWidget;

    if (!updatedWidget) {
      return;
    }

    reset({ name: updatedWidget.name, url: updatedWidget.url });
  }

  function onSubmit({ name, url }: IframeWidgetFieldValues) {
    if (!dashboard) {
      return;
    }

    const id = widget?.id || crypto.randomUUID();

    const layout: Omit<RGL.Layout, 'i'> =
      widget?.layout ||
      getNewWidgetLayout(dashboard.configuration.widgets, 5, 5);

    const updatedDashboard = upsertWidget(
      dashboard,
      new IframeWidget(id, name, url, layout)
    );

    mutate({
      id: updatedDashboard.id,
      configuration: updatedDashboard.configuration,
    });
  }

  return (
    <form className="flex h-full flex-col" onSubmit={handleSubmit(onSubmit)}>
      <WidgetFormHeader type="iframe" />
      <Field label="Name" error={errors.name}>
        <Input
          {...register('name', {
            required: 'Please choose a name for this widget.',
          })}
          type="text"
          autoComplete="off"
          spellCheck="false"
        />
      </Field>
      <Field label="URL" error={errors.url}>
        <Input
          {...register('url', {
            required: 'Please specify a url for this widget.',
          })}
        />
      </Field>

      <WidgetFormFooter isDirty={isDirty} isLoading={isUpdating} />
    </form>
  );
}
